import { AgoraVideoPlayer } from 'agora-rtc-react';
import AgoraRTC, {
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  IAgoraRTCRemoteUser,
  ILocalVideoTrack,
} from 'agora-rtc-sdk-ng';
import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEffect } from 'react';
import { AiOutlineFundProjectionScreen } from 'react-icons/ai';
import { AppState, store } from '../../../../store/store';
import { BsImage } from 'react-icons/bs';
import {
  useEndDeliveryLiveMutation,
  useEndLiveCompetitionsMutation,
  useEndLiveMatchMutation,
  useGetListPairMutation,
} from '../../../../services/api/apiProvider';
import { LiveProps } from '../../LiveContainer';
import { LiveEventItem } from '../LiveEvents/ListEventItem';
import { ListBoard } from '../LiveListBoard/LiveListBoard';
import ButtonStrMatch from '../../../../assets/svg/btn_str_match.svg';
import './LiveMediaPlayerHost.scss';
import LiveMatchListModal from '../../../live-match/components/LiveMatchListModal/LiveMatchListModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleLiveMatchList,
  updateLiveMatchData,
  updateLiveMatchStatus,
  updateLiveCompetitions,
  resetDataLiveMatch,
  updateWidthTicker,
  updateFinishLiveCompetition,
} from '../../../live-match/liveMatchSlice';
import LiveMatchRequestModal from '../../../live-match/components/LiveMatchRequestModal/LiveMatchRequestModal';
import { VoiceGuestModal } from '../VoiceGuest/VoiceGuestModal';
import { VoiceGuestBtn } from '../VoiceGuest/VoiceGuestBtn';
import { MessageType } from '../../types/messageType';
import { RtmMessage } from 'agora-rtm-sdk';
import { Debug } from '../../../../common/utils/debugLogs';
import LiveMatch from '../../../live-match/components/LiveMatch/LiveMatch';
import LiveMatchEndModal from '../../../live-match/components/LiveMatchEndModal/LiveMatchEndModal';
import LiveMatchTicker from '../../../live-match/components/LiveMatchButton/LiveMatchTicker';
import { LiveMatchStartEnd } from '../../../live-match/types/LiveMatchTypes';
import { convertDataToUID } from '../../../../common/utils/utils';
import { LiveCompetitionResult } from '../../../../services/api/models/response/responseLiveMatch';
import { RequestEndLiveCompetition } from '../../../../services/api/models/request/requestLiveMatch';
import remoteUserImgNone from '../../../../assets/png/img_none.png';
import { actions } from '../../liveSlice';
import { enableLiveMatch } from '../LiveSettings/LiveSettings';
import MobileDetect from 'mobile-detect';

export const LiveMediaPlayerHost = (props: LiveProps): ReactElement => {
  const dispatch = useDispatch();
  const liveMatchState = useSelector((state: AppState) => state.liveMatch);
  const liveData = useSelector((state: AppState) => state.live.liveData);
  const [stopDelivery] = useEndDeliveryLiveMutation();
  const [endLiveCompetitions] = useEndLiveCompetitionsMutation();
  const [endLiveMatch] = useEndLiveMatchMutation();
  const [getListPair] = useGetListPairMutation();
  /** Check that tracks were published? */
  const didPublishRef = useRef(false);
  const cameraTrackRef = useRef<ICameraVideoTrack>();
  const customVideoTrackRef = useRef<ILocalVideoTrack>();
  const microphoneTrackRef = useRef<IMicrophoneAudioTrack>();
  const [ratio, setRatio] = useState<number>();
  /** Remote user on the channel such as Agora OBS */
  const [remoteUser, setRemoteUser] = useState<IAgoraRTCRemoteUser>();
  /** Use for force re-render component */
  const [rerender, setRerender] = useState(false);
  const [backgroundImg, setBackgroundImg] = useState<string | undefined>(
    props.liveState.liveData?.user.picture.uri,
  );
  //check if target user quit live match
  const isTargetQuitRef = useRef(false);
  const drawBackgroundInterval = useRef<NodeJS.Timeout>();

  const uidRTC = useMemo(() => {
    if (!props.liveState.isUseStreamKey)
      return convertDataToUID(props.liveState.liveData?.user.id) || '';
    return null;
  }, [props.liveState.liveData?.user.id, props.liveState.isUseStreamKey]);

  // side effect join channel
  useEffect(() => {
    if (!props.liveState.isInLive || !props.liveState.liveData) {
      return;
    }
    // set role
    props.liveState.rtcEngine?.instance?.setClientRole(props.liveState.role);

    // join channel
    props.liveState.rtcEngine
      ?.joinChannel(props.liveState.liveData.id, uidRTC)
      ?.then(() => {
        //Public video and audio if no using streamKey
        if (!props.liveState.isUseStreamKey) {
          // Check has any streaming of remote users in the channel.
          // In case the channel is having a video stream (like from Agora OBS), no publish tracks.
          setTimeout(async () => {
            const remoteUserCount =
              props.liveState.rtcEngine?.instance?.remoteUsers.length;
            if (!remoteUserCount || remoteUserCount <= 0) {
              // publish tracks
              await updateCameraTrack(props.liveState.inputVideoDevice);
              await updateMicrophoneTrack(props.liveState.inputAudioDevice);
              didPublishRef.current = true;
            }
          }, 500);
        }
      })
      .catch((error) => {
        alert(error);
      });
    props.liveState.rtcEngine?.removeAllListeners('user-published');
    props.liveState.rtcEngine?.on('user-published', showRemoteUserStream);

    listenChannelMessage();

    // clean up
    return () => {
      const { isEnded } = store.getState().live;
      // stop delivery for liver when this component unmount
      if (props.liveState.liveData?.id !== undefined && !isEnded) {
        // turn off camera and microphone tracks
        cameraTrackRef.current?.close();
        microphoneTrackRef.current?.close();
        stopDelivery(props.liveState.liveData?.id).unwrap();
      }
    };
  }, [props.liveState.isInLive]);

  useEffect(() => {
    props.liveState.rtcEngine?.on(
      'user-unpublished',
      handleRemoteUserUnpublish,
    );
    return () => {
      props.liveState.rtcEngine?.removeAllListeners('user-unpublished');
    };
  }, [
    props.liveState.isInLive,
    liveMatchState.liveCompetitions,
    liveMatchState.isFinishLiveCompetition,
  ]);

  // Ratio effect. Resize video
  useEffect(() => {
    resizeVideo();

    const onWindowResize = () => {
      resizeVideo();
    };
    window.addEventListener('resize', onWindowResize);

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, [ratio]);

  // side effect for update IO media device
  useEffect(() => {
    if (didPublishRef.current) {
      updateCameraTrack(props.liveState.inputVideoDevice);
    }
  }, [props.liveState.inputVideoDevice]);

  useEffect(() => {
    if (didPublishRef.current) {
      updateMicrophoneTrack(props.liveState.inputAudioDevice);
    }
  }, [props.liveState.inputAudioDevice]);

  // side effect for update background voice
  useEffect(() => {
    if (didPublishRef.current) {
      handleUpdateImageVoice();
    }
  }, [backgroundImg]);

  /**
   * Show video and play audio of remote user.
   * Firstly, unpublish current stream of the host.
   * @param remoteUser remote user
   * @param mediaType video or audio
   */
  const showRemoteUserStream = async (
    remoteUser: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video',
  ) => {
    // subcribe and play resource of joined user
    await props.liveState.rtcEngine?.instance?.subscribe(remoteUser, mediaType);
    if (mediaType === 'video') {
      setRemoteUser(remoteUser);
      // resize video player
      remoteUser.videoTrack?.on('first-frame-decoded', () => {
        const videoSize = remoteUser.videoTrack?.getCurrentFrameData();
        if (videoSize) {
          setRatio(videoSize.width / videoSize.height);
        }
      });
    }

    if (mediaType === 'audio') {
      remoteUser.audioTrack?.play();
    }
  };

  const handleRemoteUserUnpublish = (
    unpublishUser: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video',
  ) => {
    if (mediaType === 'audio') {
      return;
    }

    const { liveCompetitions, isFinishLiveCompetition } = liveMatchState;
    if (liveCompetitions && !isFinishLiveCompetition) {
      const winnerId =
        liveData?.id === liveCompetitions?.liveMatch.live.id
          ? liveCompetitions.liveMatch.live.user.id
          : liveCompetitions.liveMatch.target.user.id;
      const request: RequestEndLiveCompetition = {
        id: liveCompetitions.id,
        winner: winnerId,
      };
      endLiveCompetitions(request).unwrap();
      dispatch(updateFinishLiveCompetition(true));
      setTimeout(() => {
        endLiveMatch(liveCompetitions.liveMatch.id).unwrap();
      }, 5000);
    } else {
      removeRemoteUserStream(unpublishUser, mediaType);
    }
  };

  /**
   * Remove video and audio of remote user.
   * Restart stream of the host.
   * @param remoteUser remote user
   * @param mediaType video or audio
   */
  const removeRemoteUserStream = async (
    remoteUser: IAgoraRTCRemoteUser,
    mediaType: 'audio' | 'video',
  ) => {
    await props.liveState.rtcEngine?.instance?.unsubscribe(
      remoteUser,
      mediaType,
    );

    if (mediaType === 'video') {
      setRemoteUser(undefined);
    }
  };

  const handleOpenListMatchModal = () => {
    dispatch(toggleLiveMatchList(true));
  };
  const resizeVideo = () => {
    if (!ratio) {
      return;
    }

    const md = new MobileDetect(window.navigator.userAgent);
    if (md.os() === 'AndroidOS' || md.os() === 'iOS') {
      return;
    }

    const element = document.querySelector(
      '.live-media-player__content',
    ) as HTMLDivElement;
    if (!element) {
      return;
    }

    const newWidth = Math.round(element.clientHeight * ratio);
    element.style.width = liveMatchState.liveMatchData
      ? `${newWidth * 1.7}px`
      : `${newWidth}px`;
    element.style.width = `${newWidth}px`;

    dispatch(actions.toggleResizeStatus());
  };

  const callbackVoiceGuestBtn = async () => {
    const data = await getListPair({
      id: props.liveState.liveData?.radio?.id,
    }).unwrap();
    if (data) {
      props.updateListPair(data);
    }
    props.toggleVoiceGuestModal(true);
  };

  /**
   * Update camera track device.
   * Turn off and unpublish camera if `deviceId` is `undefined`.
   * Turn on and publish the new camera track if camera is off.
   * @param deviceId camera device id, `undefined` is stop camera.
   */
  const updateCameraTrack = async (deviceId?: string) => {
    const cameraTrack = cameraTrackRef.current;

    if (!deviceId) {
      publishVoiceBackground();
    } else if (deviceId) {
      if (cameraTrack) {
        await cameraTrack.setDevice(deviceId);
      } else {
        try {
          const camTrack = await AgoraRTC.createCameraVideoTrack({
            cameraId: deviceId,
          });
          cameraTrackRef.current = camTrack;
          props.liveState.rtcEngine?.publishStream(camTrack);
        } catch (error) {
          console.log(error);
        }
      }
      // set new ratio video
      const settings = cameraTrackRef.current
        ?.getMediaStreamTrack()
        .getSettings();
      if (settings && settings.width && settings.height) {
        setRatio(settings.width / settings.height);
        setRerender(!rerender);
      }
    } else {
      if (cameraTrack) {
        await props.liveState.rtcEngine?.unpublishStream(cameraTrack);
        cameraTrack.close();
        cameraTrackRef.current = undefined;
      }
    }
  };

  const publishVoiceBackground = async () => {
    const canvasElt = document.querySelector('canvas') as HTMLCanvasElement;
    const img = document.getElementById(
      'background_canvas',
    ) as HTMLImageElement;
    const stream = canvasElt.captureStream(30);
    const canvasContext = canvasElt.getContext('2d');
    if (canvasContext) {
      canvasContext.canvas.width = img.width;
      canvasContext.canvas.height = img.height;
    } else {
      Debug.error('UpdateImageVoice error: No canvasContext');
    }
    clearInterval(drawBackgroundInterval.current);
    drawBackgroundInterval.current = undefined;
    if (img !== null) {
      drawBackgroundInterval.current = setInterval(() => {
        canvasContext?.drawImage(img, 0, 0, img.width, img.height);
      }, 1000 / 30);
    }
    const videoTracks = stream.getVideoTracks();
    const camTrack = AgoraRTC.createCustomVideoTrack({
      mediaStreamTrack: videoTracks[0],
    });
    customVideoTrackRef.current = camTrack;
    props.liveState.rtcEngine?.publishStream(camTrack);
    setRerender((rerender) => !rerender);
    const settings = customVideoTrackRef.current
      ?.getMediaStreamTrack()
      .getSettings();
    if (settings && settings.width && settings.height) {
      setRatio(settings.width / settings.height);
      setRerender(!rerender);
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(drawBackgroundInterval.current);
    };
  }, []);

  const handleChangeBackground = (event: any) => {
    const { files } = event.target;
    if (FileReader && files && files.length > 0) {
      const fr = new FileReader();
      fr.onload = () => {
        setBackgroundImg(fr.result as string);
      };
      fr.readAsDataURL(files[0]);
    }
  };

  const handleUpdateImageVoice = async () => {
    const customVideoTrack = customVideoTrackRef.current;
    if (customVideoTrack) {
      await props.liveState.rtcEngine?.unpublishStream(customVideoTrack);
      customVideoTrack.close();
      customVideoTrackRef.current = undefined;
      setRerender((rerender) => !rerender);
      updateCameraTrack(props.liveState.inputVideoDevice);
    }
  };

  const fetchListPairData = async () => {
    await getListPair({ id: props.liveState.liveData?.radio?.id })
      .unwrap()
      .then((response) => {
        props.updateListPair(response);
      });
  };
  // listen change message channel
  const listenChannelMessage = () => {
    props.liveState.rtmEngine?.on(
      'ChannelMessage',
      async (message: RtmMessage): Promise<void> => {
        if (message.messageType === 'TEXT') {
          const content = JSON.parse(message.text);
          if (content.type === MessageType.LEAVE_USER) {
            const userLeave = content.value;
            if (
              props.liveState.listPair.length <= 0 ||
              userLeave.id === props.liveState.userPair?.id
            ) {
              props.updateUserPair(undefined);
            }
          }
          if (content.type === MessageType.END_LIVE) {
            const liveState = store.getState().live;
            if (liveState.liveData !== undefined) {
              props.endLive();
            }
          }
          // audience send start request pair
          if (
            content.type === MessageType.START_PAIR_REQUEST ||
            content.type === MessageType.END_PAIR_REQUEST ||
            content.type === MessageType.END_PAIR
          ) {
            Debug.log('START ACCEPT PAIR');
            props.handleChangeButtonRequest(false);
            await fetchListPairData();
          }
          if (content.type === MessageType.START_ACCEPTING_PAIR) {
            props.handleChangePairType('pair');
          }
          if (content.type === MessageType.END_ACCEPTING_PAIR) {
            props.handleChangePairType('none');
          }
          if (content.type === MessageType.END_PAIR) {
            props.updateUserPair(undefined);
          }
          if (content.type === MessageType.REQUEST_LIVE_MATCH) {
            dispatch(updateLiveMatchData(content.value.liveMatch));
            dispatch(updateWidthTicker(105));

            const element: HTMLElement | null =
              document.getElementById('ticker-live-match');
            if (element) {
              element.style.transform = 'translate(0%, 0)';
            }
          }
          if (content.type === MessageType.START_LIVE_MATCH) {
            dispatch(updateLiveMatchData(content.value.liveMatch));
            dispatch(updateLiveMatchStatus(LiveMatchStartEnd.START));
            props.liveState.rtcEngine?.leaveChannel().then(() => {
              props.liveState.rtcEngine?.instance
                ?.setClientRole('host')
                .then(() => {
                  props.liveState.rtcEngine
                    ?.joinChannel(
                      content.value.liveMatch.id,
                      uidRTC,
                      'publisher',
                      content.value.liveMatch.live.id,
                    )
                    ?.then(() => {
                      const cameraTrack = cameraTrackRef.current;
                      const microphoneTrack = microphoneTrackRef.current;
                      if (cameraTrack) {
                        props.liveState.rtcEngine?.publishStream(cameraTrack);
                      }
                      if (microphoneTrack) {
                        props.liveState.rtcEngine?.publishStream(
                          microphoneTrack,
                        );
                      }
                    })
                    .catch((error) => {
                      alert(error);
                    });
                  props.liveState.rtcEngine?.removeAllListeners(
                    'user-published',
                  );
                  props.liveState.rtcEngine?.on(
                    'user-published',
                    showRemoteUserStream,
                  );
                });
            });
          }
          if (content.type === MessageType.REJECT_LIVE_MATCH) {
            dispatch(updateLiveMatchData(undefined));
            dispatch(updateWidthTicker(105));
          }
          if (content.type === MessageType.START_LIVE_COMPETITION) {
            dispatch(updateLiveCompetitions(content.value.liveCompetition));
          }
          if (content.type === MessageType.NOTIFY_LIVE_COMPETITION_SCORE) {
            dispatch(updateLiveCompetitions(content.value.liveCompetition));
          }
          if (content.type === MessageType.END_LIVE_MATCH) {
            setTimeout(
              () => {
                dispatch(resetDataLiveMatch());
                setRemoteUser(undefined);
                isTargetQuitRef.current = false;
                props.liveState.rtcEngine?.leaveChannel().then(() => {
                  props.liveState.rtcEngine?.instance
                    ?.setClientRole('host')
                    .then(() => {
                      if (
                        !props.liveState.isInLive ||
                        !props.liveState.liveData
                      ) {
                        return;
                      }
                      props.liveState.rtcEngine
                        ?.joinChannel(props.liveState.liveData.id, uidRTC)
                        ?.then(() => {
                          const cameraTrack = cameraTrackRef.current;
                          const microphoneTrack = microphoneTrackRef.current;
                          if (cameraTrack) {
                            props.liveState.rtcEngine?.publishStream(
                              cameraTrack,
                            );
                          }
                          if (microphoneTrack) {
                            props.liveState.rtcEngine?.publishStream(
                              microphoneTrack,
                            );
                          }
                        })
                        .catch((error) => {
                          alert(error);
                        });
                      props.liveState.rtcEngine?.removeAllListeners(
                        'user-published',
                      );
                      props.liveState.rtcEngine?.on(
                        'user-published',
                        showRemoteUserStream,
                      );
                    });
                });
              },
              isTargetQuitRef.current ? 10000 : 0,
            );
          }
          if (content.type === MessageType.END_LIVE_COMPETITION) {
            const isTargetQuit = content.value.liveCompetition?.scores?.some(
              (score: any) =>
                score?.result === LiveCompetitionResult.Abstention,
            );
            if (isTargetQuit) {
              isTargetQuitRef.current = isTargetQuit;
              dispatch(updateFinishLiveCompetition(true));
            }
            dispatch(updateLiveCompetitions(content.value.liveCompetition));
          }
          if (content.type === MessageType.NOTIFY_EVENT_RANKING) {
            dispatch(props.updateEventRankings(content.value.eventRankings));
          }
        }
      },
    );
  };

  /**
   * Update microphone device.
   * Turn off and unpublish audio if `deviceId` is `undefined`.
   * Turn on and publish the new audio track if audio is off.
   * @param deviceId audio device id, `undefined` is turning off microphone.
   */
  const updateMicrophoneTrack = async (deviceId?: string) => {
    const microphoneTrack = microphoneTrackRef.current;
    if (deviceId) {
      if (microphoneTrack) {
        microphoneTrack.setDevice(deviceId);
      } else {
        const micTrack = await AgoraRTC.createMicrophoneAudioTrack({
          microphoneId: deviceId,
          encoderConfig: 'high_quality_stereo',
        });
        microphoneTrackRef.current = micTrack;
        props.liveState.rtcEngine?.publishStream(micTrack);
      }
    } else {
      if (microphoneTrack) {
        await props.liveState.rtcEngine?.unpublishStream(microphoneTrack);
        microphoneTrack.close();
        microphoneTrackRef.current = undefined;
      }
    }
  };
  /**
   * Render video player
   */
  const renderVideoPlayer = useCallback(() => {
    const cameraTrack = cameraTrackRef.current;
    const customVideoTrack = customVideoTrackRef.current;
    return (
      <>
        {cameraTrack && (
          <AgoraVideoPlayer
            videoTrack={cameraTrack}
            className="live-media-player__video"
          />
        )}

        {remoteUser && (
          <>
            {remoteUser.videoTrack && (
              <AgoraVideoPlayer
                videoTrack={remoteUser.videoTrack}
                className="live-media-player__video"
              />
            )}
            {!remoteUser.videoTrack && (
              <img
                src={remoteUserImgNone}
                className="live-media-player__content__none-img"
              />
            )}
            <LiveMatch />
          </>
        )}
        {customVideoTrack && (
          <AgoraVideoPlayer
            videoTrack={customVideoTrack}
            className="live-media-player__video"
          />
        )}
      </>
    );
  }, [remoteUser]);

  // not join or start live yet!
  if (!props.liveState.isInLive || props.liveState.liveData === undefined) {
    return <></>;
  }

  return (
    <>
      {renderVideoPlayer()}
      <img
        crossOrigin="anonymous"
        id="background_canvas"
        src={backgroundImg}
        alt="background-img"
        style={{ display: 'none', width: '100%' }}
      />
      <canvas
        id="canvas"
        style={{ width: '100%', height: '100%', display: 'none' }}
      ></canvas>
      {props.liveState.isLive ? (
        <>
          <LiveMatchTicker />
        </>
      ) : (
        <VoiceGuestBtn
          callBackToggleModal={callbackVoiceGuestBtn}
          audientGuest={props.liveState.userPair}
        />
      )}
      <div
        className={`live-control ${
          props.liveState.isLive ? '' : 'live-control--voice'
        }`}
      >
        {props.liveState.isLive ? (
          <></>
        ) : (
          <label
            className="live-control__btn d-flex"
            htmlFor="input-background-img"
          >
            <input
              id="input-background-img"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleChangeBackground}
            />
            <BsImage />
          </label>
        )}
        <div
          className="live-control__btn d-flex"
          onClick={() => {
            props.showListBoard(true);
          }}
        >
          <AiOutlineFundProjectionScreen />
        </div>
        {/* pending live match request */}
        {enableLiveMatch && (
          <div
            onClick={handleOpenListMatchModal}
            className="live-control__btn d-flex"
          >
            <img src={ButtonStrMatch} alt="btn-str-match" />
          </div>
        )}
      </div>

      {props.liveState.isShowListBoard && (
        <div className="live-media-player__list-board">
          <ListBoard />
        </div>
      )}

      {/* Live Event */}
      <div className="live-event-banner__list">
        {props.liveState.isEnableGift &&
          props.liveState.liveData?.events
            ?.slice()
            ?.reverse()
            .map((item) => {
              const eventItemProps = {
                liveProps: props,
                event: item.event,
              };
              return (
                <LiveEventItem
                  key={item.event?.id}
                  {...eventItemProps}
                ></LiveEventItem>
              );
            })}
      </div>
      {liveMatchState.isShowListMatch && <LiveMatchListModal />}
      {liveMatchState.isShowLiveMatchRequest && <LiveMatchRequestModal />}
      {liveMatchState.isEndLiveMatch && <LiveMatchEndModal />}
      <VoiceGuestModal {...props} />
    </>
  );
};
